import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Linkedin,
  Instagram,
  ArrowUp
} from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <h3 className="text-2xl font-bold text-[#4b9d96] mb-4">LST OIL SA</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Votre partenaire de confiance pour la logistique, les services et le transport.
              Solutions complètes et innovantes pour tous vos besoins.
            </p>
            
            <div className="flex space-x-4">
            <a
                href="https://wa.me/+221775577748"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-green-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </a>
              <a href="https://www.facebook.com/people/Lst-Oil-SA/pfbid02VAYXDp2RqkjCPjPcqCzTmfNy1gFxpysXX8Q2fGWJahAurNzrcDeXGq9VDXuz5R7Kl/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#4b9d96] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              
              <a href="https://www.linkedin.com/in/lst-oil-sa-697346386/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#4b9d96] transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/lst.oil.sa/?__pwa=1%2F" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#4b9d96] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold mb-4 text-[#4b9d96]">Liens Rapides</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  À propos
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Nos Services
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Produits
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold mb-4 text-[#4b9d96]">Nos Services</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services#bases" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                 Hydrocarbures & BTP
                </Link>
              </li>
              <li>
                <Link to="/services#logistique" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Logistique
                </Link>
              </li>
              <li>
                <Link to="/services#alimentaires" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Produits Alimentaires
                </Link>
              </li>
              
              
              <li>
                <Link to="/services#conseil" className="text-gray-300 hover:text-[#4b9d96] transition-colors">
                  Consultance
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold mb-4 text-[#4b9d96]">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#4b9d96] mt-1 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  Liberté 6, Rond Point Pentola<br />
                  Dakar, Sénégal<br />

                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#4b9d96] flex-shrink-0" />
                <p className="text-gray-300 text-sm">+221 33 802 84 84</p>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[#4b9d96] flex-shrink-0" />
                <a
                  href="mailto:lst@lstoil.com"
                  className="text-gray-300 hover:text-[#4B9D96] transition-colors duration-300 cursor-pointer"
                >
                  lst@lstoil.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-[#4b9d96] flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  Lun-Ven: 8h-17h<br />

                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} LST OIL SA. Tous droits réservés. |
              <Link to="/privacy" className="hover:text-[#4b9d96] transition-colors ml-1">
                Politique de confidentialité
              </Link> |
              <Link to="/terms" className="hover:text-[#4b9d96] transition-colors ml-1">
                Conditions d'utilisation
              </Link>
            </p>
            <button
              onClick={scrollToTop}
              className="mt-4 md:mt-0 bg-[#4b9d96] hover:bg-[#4b9d96] text-gray-900 p-2 rounded-full transition-all duration-300 transform hover:scale-110"
              aria-label="Retour en haut"
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

